import { HttpErrorResponse } from '@angular/common/http';
import { GetTranslationsDto, IGroupEntityDTO } from '@modeso/types__aumico-lib-templates-be';
import { EntityAdapter, EntityState, createEntityAdapter } from '@ngrx/entity';
import { Action, createReducer, createSelector, on } from '@ngrx/store';
import Debug from 'debug';
import * as fromTemplatesActions from '../actions/templates.actions';

const debug = Debug('aumico:aumico-lib-templates-fe:GroupsReducer');

export const groupFeatureKey = 'groups';

export interface GroupState extends EntityState<IGroupEntityDTO> {
    activeGroup: string | null;
    selectedGroupsIds: string[];
    response: IGroupEntityDTO;
    translations: GetTranslationsDto | null;
    error: HttpErrorResponse;
}

export function selectGroupId(group: IGroupEntityDTO): string {
    return group._id;
}

export const adapter: EntityAdapter<IGroupEntityDTO> = createEntityAdapter<IGroupEntityDTO>({
    selectId: selectGroupId,
});

export const { selectEntities, selectAll } = adapter.getSelectors();

export const selectAllGroups = selectAll;

export const getSelectedGroupsIds = (state: GroupState) => state.selectedGroupsIds;
export const selectGroups = createSelector(
    selectAll,
    getSelectedGroupsIds,
    (groups: IGroupEntityDTO[], selectedGroupsIds: string[]) => {
        return groups.filter((group) => !selectedGroupsIds.includes(group._id) && !group.isDefault);
    },
);

export const selectDefaultGroups = createSelector(selectAll, (groups: IGroupEntityDTO[]) => {
    const defaultGroupsMap: Map<string, IGroupEntityDTO> = new Map<string, IGroupEntityDTO>();
    const defaultGroups = groups.filter((gp) => gp.isDefault);
    for (let group of defaultGroups) {
        defaultGroupsMap.set(group.name, group);
    }
    return defaultGroupsMap;
});

export const getActiveGroupId = (state: GroupState) => state.activeGroup;
export const getActiveGroup = createSelector(selectEntities, getActiveGroupId, (entities, id) => {
    return entities[id] ? Object.assign({}, entities[id]) : entities[id];
});

export const getGroupsResponse = (state: GroupState) => state.response;
export const getGroupsError = (state: GroupState) => state.error;
export const getTranslations = (state: GroupState) => state.translations;

export const getGroupNameExists = createSelector(
    selectEntities,
    (entities, props: { name: string; groupId: string }) => {
        const keys = Object.keys(entities);
        for (const key of keys) {
            const group: IGroupEntityDTO = entities[key];
            if (group.name == props.name && group._id != props.groupId) {
                return true;
            }
        }
        return false;
    },
);

export const getEquityGroupOptions = createSelector(selectAllGroups, (groups: IGroupEntityDTO[]) => {
    return groups.map((group) => group.name);
});

export const initialGroupState: GroupState = adapter.getInitialState({
    activeGroup: null,
    selectedGroupsIds: [],
    response: undefined,
    error: undefined,
    translations: null,
});

const groupsReducer = createReducer(
    initialGroupState,

    // Get Groups
    on(fromTemplatesActions.getGroups, (state) => ({
        ...state,
    })),
    on(fromTemplatesActions.onGetGroupsSuccessfully, (state, action) => {
        return adapter.setAll(action.payload, {
            ...state,
        });
    }),
    on(fromTemplatesActions.onGetGroupsFailed, (state, action) => ({
        ...state,
        error: action.payload,
    })),

    // Active Group
    on(fromTemplatesActions.setActiveGroup, (state, action) => ({
        ...state,
        activeGroup: action.payload,
    })),
    on(fromTemplatesActions.removeActiveGroup, (state) => ({
        ...state,
        activeGroup: null,
    })),

    // Selected Groups
    on(fromTemplatesActions.selectGroups, (state, action) => ({
        ...state,
        selectedGroupsIds: [...state.selectedGroupsIds, ...action.payload],
    })),
    on(fromTemplatesActions.deselectGroup, (state, action) => ({
        ...state,
        selectedGroupsIds: [...state.selectedGroupsIds].filter((groupId) => groupId != action.payload),
    })),
    on(fromTemplatesActions.removeActiveTemplate, (state) => ({
        ...state,
        selectedGroupsIds: [],
    })),

    // Create Group
    on(fromTemplatesActions.createGroup, (state) => ({
        ...state,
    })),
    on(fromTemplatesActions.onCreateGroupSuccessfully, (state, action) => {
        return adapter.addOne(action.payload, {
            ...state,
            response: action.payload,
        });
    }),
    on(fromTemplatesActions.onCreateGroupFailed, (state, action) => ({
        ...state,
        error: action.payload,
    })),

    // Update Group
    on(fromTemplatesActions.updateGroup, (state) => ({
        ...state,
    })),
    on(fromTemplatesActions.onUpdateGroupSuccessfully, (state, action) => {
        return adapter.updateOne(
            { id: action.payload._id, changes: action.payload },
            {
                ...state,
                response: action.payload,
            },
        );
    }),
    on(fromTemplatesActions.onUpdateGroupFailed, (state, action) => ({
        ...state,
        error: action.payload,
    })),

    // Delete Group
    on(fromTemplatesActions.deleteGroup, (state) => ({
        ...state,
    })),
    on(fromTemplatesActions.onDeleteGroupSuccessfully, (state, action) => {
        return adapter.removeOne(action.payload, {
            ...state,
            response: undefined,
        });
    }),
    on(fromTemplatesActions.onDeleteGroupFailed, (state, action) => ({
        ...state,
        error: action.payload,
    })),

    // Response and error
    on(fromTemplatesActions.removeGroupsResponse, (state) => ({
        ...state,
        response: undefined,
    })),
    on(fromTemplatesActions.removeGroupsError, (state) => ({
        ...state,
        error: undefined,
    })),

    // Translations
    on(fromTemplatesActions.onGetTranslationsSuccess, (state, action) => ({
        ...state,
        translations: action.payload,
    })),
    on(fromTemplatesActions.onGetTranslationsFailed, (state, action) => ({
        ...state,
        error: action.payload,
    })),
);

export function reducer(state: GroupState | undefined, action: Action) {
    return groupsReducer(state, action);
}
